import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth.jsx";
import { BookOpen, Map as MapIcon, Timer, User, LogOut, Menu, X } from "lucide-react";
import PWAInstall from "./PWAInstall.jsx";
import { InstallButton } from "./InstallButton.jsx";

export default function AppLayout() {
  const { user, access, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const navItems = [
    { to: "/dashboard", label: "Study", icon: BookOpen, testid: "nav-study" },
    { to: "/mock-test", label: "Mock Test", icon: Timer, testid: "nav-mock" },
    { to: "/map-work", label: "Map Work", icon: MapIcon, testid: "nav-map" },
    { to: "/profile", label: "Profile", icon: User, testid: "nav-profile" },
  ];

  return (
    <div className="min-h-screen bg-brimi-cream">
      <header className="sticky top-0 z-30 backdrop-blur bg-brimi-cream/85 border-b border-brimi-creamSoft">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="font-heading font-bold text-xl text-brimi-navy flex items-center gap-2" data-testid="brand-link">
            <span>Brimi<span className="text-brimi-terracotta">H</span>&<span className="text-brimi-sage">G</span></span>
            {user?.is_owner && (
              <span
                className="brimi-pill bg-brimi-navy text-white text-[10px] !px-2 !py-0.5"
                title="Owner account"
                data-testid="owner-badge-header"
              >
                ★ Owner
              </span>
            )}
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition ${
                    isActive ? "bg-brimi-navy text-white" : "text-brimi-navyText hover:bg-brimi-creamSoft"
                  }`
                }
              >
                <n.icon size={16} /> {n.label}
              </NavLink>
            ))}
            <InstallButton variant="header" />
            <button
              data-testid="logout-btn"
              onClick={() => {
                logout();
                navigate("/");
              }}
              className="ml-2 brimi-btn-ghost"
              title="Log out"
            >
              <LogOut size={16} />
            </button>
          </nav>
          <button
            className="md:hidden p-2 rounded-lg hover:bg-brimi-creamSoft"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-btn"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-brimi-creamSoft bg-brimi-cream">
            <div className="max-w-6xl mx-auto px-5 py-3 flex flex-col gap-1">
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  data-testid={`m-${n.testid}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-xl text-sm ${
                      isActive ? "bg-brimi-navy text-white" : "text-brimi-navyText hover:bg-brimi-creamSoft"
                    }`
                  }
                >
                  <n.icon size={16} /> {n.label}
                </NavLink>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  logout();
                  navigate("/");
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm hover:bg-brimi-creamSoft text-left"
                data-testid="m-logout-btn"
              >
                <LogOut size={16} /> Log out
              </button>
              <div className="pt-1">
                <InstallButton variant="ghost" />
              </div>
            </div>
          </div>
        )}
      </header>

      {access && access.is_active && access.status === "trial" && (
        <div className="bg-brimi-terracotta/15 border-b border-brimi-terracotta/30">
          <div className="max-w-6xl mx-auto px-5 py-2 flex items-center justify-between text-sm">
            <span className="text-brimi-navyText">
              Free trial — <strong>{access.days_left} day{access.days_left === 1 ? "" : "s"}</strong> left
            </span>
            <Link to="/paywall" className="font-semibold text-brimi-terracotta hover:underline" data-testid="trial-upgrade-link">
              Upgrade
            </Link>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-5 pt-8 pb-24 md:pb-12">
        <Outlet />
      </main>

      <PWAInstall />
    </div>
  );
}
